/**
 * Service pour la gestion des sites et la synchronisation multi-tenant
 */
import apiService from './api';

export interface Site {
  id: number;
  code: string;
  name: string;
  site_type: string;
  address: string;
  is_active: boolean;
  tenant: {
    id: number;
    name: string;
    code: string;
  };
  // Propriétés supplémentaires des UserSiteAssignment
  is_default?: boolean;
  role?: {
    id: number;
    name: string;
    description?: string;
  };
  created_at?: string;
}

export interface Tenant {
  id: number;
  name: string;
  code: string;
  is_active: boolean;
}

export interface SiteSwitchResponse {
  site: Site;
  message: string;
}

class SitesService {
  /**
   * Récupérer la liste des sites
   */
  async getSites(): Promise<Site[]> {
    try {
      const response = await apiService.get('/sites/');
      const data = response.data;
      return data.results || data || [];
    } catch (error) {
      console.error('Erreur lors de la récupération des sites:', error);
      return [];
    }
  }

  /**
   * Récupérer la liste des tenants
   */
  async getTenants(): Promise<Tenant[]> {
    try {
      const response = await apiService.get('/sites/tenants/');
      const data = response.data;
      
      // S'assurer que nous retournons toujours un tableau
      if (Array.isArray(data)) {
        return data;
      } else if (data && Array.isArray(data.results)) {
        return data.results;
      } else if (data && Array.isArray(data.data)) {
        return data.data;
      } else {
        console.warn('Format de données inattendu pour les tenants:', data);
        return [];
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des tenants:', error);
      return [];
    }
  }

  /**
   * Changer de site actif
   */
  async switchSite(siteCode: string): Promise<SiteSwitchResponse | null> {
    try {
      const response = await apiService.post('/sites/switch/', { site_code: siteCode });
      return response.data;
    } catch (error) {
      console.error('Erreur lors du changement de site:', error);
      return null;
    }
  }

  /**
   * Récupérer le site actif
   */
  async getActiveSite(): Promise<Site | null> {
    try {
      const response = await apiService.get('/sites/active/');
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération du site actif:', error);
      return null;
    }
  }

  /**
   * Récupérer les sites accessibles à l'utilisateur
   */
  async getMySites(): Promise<Site[]> {
    try {
      const response = await apiService.get('/sites/my-sites/');
      const data = response.data;
      
      // L'API retourne des UserSiteAssignment objects avec une propriété 'site' imbriquée
      const userSiteAssignments = data.results || data || [];
      
      // Extraire les sites des UserSiteAssignment objects
      const sites = userSiteAssignments.map((assignment: any) => {
        if (assignment.site) {
          // Ajouter les propriétés supplémentaires du UserSiteAssignment au site
          return {
            ...assignment.site,
            is_default: assignment.is_default,
            role: assignment.role,
            created_at: assignment.created_at
          };
        }
        return assignment;
      }).filter((site: Site) => site && site.id);
      
      return sites;
    } catch (error) {
      console.error('Erreur lors de la récupération des sites accessibles:', error);
      return [];
    }
  }

  /**
   * Créer un nouveau site
   */
  async createSite(siteData: Partial<Site>): Promise<Site | null> {
    try {
      const response = await apiService.post('/sites/create/', siteData);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la création du site:', error);
      return null;
    }
  }

  /**
   * Mettre à jour un site
   */
  async updateSite(siteId: number, siteData: Partial<Site>): Promise<Site | null> {
    try {
      const response = await apiService.patch(`/sites/${siteId}/`, siteData);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour du site:', error);
      return null;
    }
  }

  /**
   * Supprimer un site
   */
  async deleteSite(siteId: number): Promise<boolean> {
    try {
      await apiService.delete(`/sites/${siteId}/`);
      return true;
    } catch (error) {
      console.error('Erreur lors de la suppression du site:', error);
      return false;
    }
  }

  /**
   * Bloquer un site
   */
  async blockSite(siteId: number, reason?: string): Promise<boolean> {
    try {
      const data = reason ? { reason } : {};
      await apiService.post(`/sites/${siteId}/block/`, data);
      return true;
    } catch (error) {
      console.error('Erreur lors du blocage du site:', error);
      return false;
    }
  }

  /**
   * Débloquer un site
   */
  async unblockSite(siteId: number): Promise<boolean> {
    try {
      await apiService.post(`/sites/${siteId}/unblock/`);
      return true;
    } catch (error) {
      console.error('Erreur lors du déblocage du site:', error);
      return false;
    }
  }
}

const sitesService = new SitesService();
export default sitesService;
